import { ATHLETE } from './athlete';
import { diffDefinitions } from '../../utils/gpb/releaseDiff';
import type { GpbVersionDiff } from '../../utils/gpb/releaseDiff';
import type { GpbDefinition, GpbRelease } from '../../types/gpb';

/**
 * ⭐⭐ **WHAT MAKES D23'S PIN REAL RATHER THAN DECORATIVE.**
 *
 * A taker is pinned to the version they applied, and an update is an offer they
 * can decline. That promise is empty unless the version they are pinned to is
 * still here to be evaluated: with a single `ATHLETE` object in the bundle,
 * publishing 0.2 would silently re-rule everybody on 0.1 — their goals would move
 * under them, which is exactly what the pin exists to prevent.
 *
 * ⚠️⚠️ **SO A PUBLISHED VERSION STAYS IN THE BUNDLE WHILE ANYBODY HOLDS IT.** That
 * is the real cost of D23 and it is paid here, in bytes: every release adds a
 * definition, and one can only be removed once no enrollment points at it.
 *
 * ⚠️ A pinned version that is missing is NOT silently upgraded. `definitionFor`
 * says `retired`, and the surface has to tell the person their pathway is being
 * read against newer rules — a thing they never agreed to. Silence there would be
 * the D23 violation wearing a shrug.
 */

const DEFINITIONS: Readonly<Record<string, Readonly<Record<string, GpbDefinition>>>> = {
  [ATHLETE.id]: { [ATHLETE.version]: ATHLETE },
};

/**
 * ⚠️⚠️ **THE BUNDLED IDS, READ-ONLY, FOR THE RESOLVER THAT MERGES THESE WITH
 * AUTHORED ONES.** `services/gpbCatalog.ts` is the only consumer, and it exists
 * because profiles can now also be written rather than compiled.
 *
 * ⚠️⚠️⚠️ **A BUNDLED ID CAN NEVER BE SHADOWED, AND THIS MAP IS HOW THAT IS
 * DECIDED.** Nothing in the database owns `athlete-home`, so the first account to
 * publish under that id would own it there — and `latestOf` would then hand
 * Athlete's takers a stranger's release as the newest version. The resolver
 * answers from here FIRST and ignores published rows for any id that appears in
 * this map. See `gpbCatalog.ts`.
 *
 * ⚠️ Everything else in this file answers about the BUNDLE ONLY. Consumers want
 * the merged view and should import from the service, not from here.
 */
export const BUNDLED = DEFINITIONS;

export type GpbResolution =
  /** The version the person is pinned to is shipped, and this is it. */
  | { status: 'pinned'; def: GpbDefinition }
  /**
   * Their version is gone. The latest is returned so the app still works, and the
   * caller MUST say so — their rules have changed without their agreement.
   */
  | { status: 'retired'; def: GpbDefinition; pinned: string }
  /** No such profile ships at all — a shared profile from a newer app, perhaps. */
  | { status: 'unknown' };

export function definitionFor(id: string, version: string): GpbResolution {
  const byVersion = DEFINITIONS[id];
  if (!byVersion) return { status: 'unknown' };
  const exact = byVersion[version];
  if (exact) return { status: 'pinned', def: exact };

  const latest = latestOf(id);
  return latest ? { status: 'retired', def: latest, pinned: version } : { status: 'unknown' };
}

/** ⚠️ Newest by publication ORDER, not by string sort — '0.10' sorts before '0.9'. */
export function latestOf(id: string): GpbDefinition | undefined {
  const all = Object.values(DEFINITIONS[id] ?? {});
  return all[all.length - 1];
}

export function versionsOf(id: string): readonly string[] {
  return Object.keys(DEFINITIONS[id] ?? {});
}

/**
 * One published version, and what it changed.
 *
 * ⚠️ `diff` is absent on a first release and on any release whose predecessor no
 * longer ships. Both are honest states and neither is an error: there is simply
 * nothing to compare against, and inventing a comparison is how a review starts
 * describing a version nobody published.
 */
export interface GpbReleaseEntry {
  def: GpbDefinition;
  release: GpbRelease;
  diff?: GpbVersionDiff;
}

/**
 * ⭐⭐ **THE RELEASE HISTORY — D26'S DOOR, AND H4'S CLOSE.**
 *
 * Oldest first, each with the change it made stated in the goals' own words. This
 * is what "the release list on Overview keeps the door open afterwards" reads.
 *
 * ⚠️⚠️ **ORDER IS PUBLICATION ORDER, NOT A STRING SORT** — `'0.10'` sorts before
 * `'0.9'`, so sorting would quietly make the newest release the oldest. It comes
 * from the order versions are declared in `DEFINITIONS`, which is the same source
 * `latestOf` trusts.
 *
 * ⚠️ Today this returns exactly one entry with no `diff`, because one version
 * ships. That is the honest answer rather than a stub, and the wiring is real:
 * the moment a second version is declared, every surface calling this shows what
 * it changed without another line of code.
 */
export function releasesOf(id: string): readonly GpbReleaseEntry[] {
  const byVersion = DEFINITIONS[id];
  return byVersion ? historyOf(byVersion) : [];
}

/**
 * The pure half of `releasesOf`, over any set of versions.
 *
 * ⚠️ Separate so the TWO-version case can be tested. One version ships, so
 * `releasesOf` exercises only the first-release branch — and the branch that
 * actually matters on the day a second release lands would otherwise ship having
 * never run. A pure function nobody calls passes its own tests perfectly (D40);
 * so does a branch no fixture reaches.
 */
export function historyOf(
  byVersion: Readonly<Record<string, GpbDefinition>>,
): readonly GpbReleaseEntry[] {
  return Object.values(byVersion).map(def => {
    const prev = def.release.supersedes ? byVersion[def.release.supersedes] : undefined;
    return prev
      ? { def, release: def.release, diff: diffDefinitions(prev, def) }
      : { def, release: def.release };
  });
}

export const CATALOGUE: readonly GpbDefinition[] =
  Object.keys(DEFINITIONS).map(id => latestOf(id)!).filter(Boolean);

/**
 * ⭐⭐ **OFFICIAL IS WHERE IT CAME FROM, NOT WHAT IT SAYS ABOUT ITSELF (D22).**
 *
 * A profile cannot declare itself first-party, because a declaration is a field
 * and a field can be copied. The authority is this module: these ids are the ones
 * the app SHIPS. A profile that arrived some other way — shared by a person,
 * imported from a link — never passes through here, so it can call its author
 * "Metanoia" all it likes and still get no badge.
 *
 * ⚠️⚠️ When user-authored profiles can be shared (D33), the same rule needs its
 * server half: an admin-only flag on the publishing ACCOUNT, checked server-side,
 * never a name and never a field on the shared payload. Until then there is no
 * way for an unofficial profile to exist, and nothing to get wrong.
 */
export function isOfficial(definitionId: string): boolean {
  return Object.prototype.hasOwnProperty.call(DEFINITIONS, definitionId);
}
